import * as React from 'react';
import { Box, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { VirtualizedAutoScrollBox } from '../virtualized-auto-scroll-box/virtualized-auto-scroll-box.component';
import {
  StatusReportTableRow,
  type StatusReportTableRowStatusTextMap,
  type StatusReportTableRowStatusIconMap,
  type StatusReportTableRowColumn,
  type StatusReportTableRendererRegistry,
  defaultStatusReportRendererRegistry,
} from '../status-report-table-row/status-report-table-row.component';
import type {
  StatusReportTableRowDetails,
  StatusReportTableRowLinkableText,
  StatusReportTableRowStatus,
} from '../status-report-table-row/status-report-table-row.types';

/** Shape of an item used by the default status-report table columns. */
export type StatusReportTableItem = {
  id: string;
  status: StatusReportTableRowStatus;
  linkableText: StatusReportTableRowLinkableText;
  details: StatusReportTableRowDetails;
};

/** message ids for the default status-report table column headers. */
export type StatusReportTableColumnLabelIdMap = {
  status: string;
  linkableText: string;
  details: string;
};

/** A status-report table column plus required header message id metadata. */
export type StatusReportTableColumn<TItem> =
  StatusReportTableRowColumn<TItem> & {
    headerId: string;
  };

const defaultColumnLabelIdMap: StatusReportTableColumnLabelIdMap = {
  status: 'STATUS',
  linkableText: 'ID',
  details: 'DETAILS',
};

/**
 * Builds the default status/link/details column set with overridable message ids.
 * Consumers can pass a custom id map when they need different header copy.
 */
export const createDefaultStatusReportColumns = (
  columnLabelIdMap: StatusReportTableColumnLabelIdMap = defaultColumnLabelIdMap,
): StatusReportTableColumn<StatusReportTableItem>[] => [
  {
    id: 'status',
    type: 'status',
    headerId: columnLabelIdMap.status,
    getValue: (item: StatusReportTableItem) => item.status,
  },
  {
    id: 'linkableText',
    type: 'linkableText',
    headerId: columnLabelIdMap.linkableText,
    getValue: (item: StatusReportTableItem) => item.linkableText,
  },
  {
    id: 'details',
    type: 'details',
    headerId: columnLabelIdMap.details,
    getValue: (item: StatusReportTableItem) => item.details,
  },
];

const defaultStatusTextMap: StatusReportTableRowStatusTextMap = {
  success: 'SUCCESS',
  skipped: 'SKIPPED',
  loading: 'LOADING',
  error: 'ERROR',
};

export type StatusReportTableProps<TItem extends { id: string }> = {
  items: TItem[];
  /**
   * Map of status values to i18n message IDs for display text.
   * @default { success: 'SUCCESS', skipped: 'SKIPPED', loading: 'LOADING', error: 'ERROR' }
   */
  statusTextMap?: StatusReportTableRowStatusTextMap;
  statusIconMap?: StatusReportTableRowStatusIconMap;
  caption?: React.ReactNode;
  columns: StatusReportTableColumn<TItem>[];
  rendererRegistry?: Partial<StatusReportTableRendererRegistry<TItem>>;
  /**
   * Returns the estimated height in pixels for a given item index.
   * If rows have a fixed height, return a constant (e.g. `() => 40`).
   * @default () => 40
   */
  estimateSize?: (index: number) => number;
} & (
  | {
      /** Accessible label for the scroll region. Use when no visible label exists. */
      ariaLabel: string;
      ariaLabelledBy?: never;
    }
  | {
      ariaLabel?: never;
      /** ID of an element that labels the scroll region. Prefer this when visible label text exists. */
      ariaLabelledBy: string;
    }
);

/**
 * Virtualized status-report table with auto-scroll. Handles virtualization,
 * scroll container, and row positioning internally — just pass data.
 *
 * For small lists the virtualizer renders all rows (zero overhead).
 * For large lists only visible rows exist in the DOM.
 *
 * @example
 * <BulkActionReportingModal title="Copying to Production" header={...} footer={...}>
 *   <StatusReportTable
 *     items={items}
 *     columns={columns}
 *   />
 * </BulkActionReportingModal>
 */
// estimateSize returns pixels because @tanstack/react-virtual operates in px space
const DEFAULT_ESTIMATED_ROW_HEIGHT = 40;
const defaultEstimateSize = () => DEFAULT_ESTIMATED_ROW_HEIGHT;

export const StatusReportTable = <TItem extends { id: string }>({
  items,
  statusTextMap = defaultStatusTextMap,
  statusIconMap,
  caption,
  columns,
  rendererRegistry,
  estimateSize = defaultEstimateSize,
  ...labelProps
}: StatusReportTableProps<TItem>) => {
  const { formatMessage } = useIntl();
  const resolvedRendererRegistry: StatusReportTableRendererRegistry<TItem> = {
    ...defaultStatusReportRendererRegistry,
    ...rendererRegistry,
  };

  return (
    <VirtualizedAutoScrollBox
      itemCount={items.length}
      estimateSize={estimateSize}
      getItemKey={(index) => items[index].id}
      {...labelProps}
    >
      {({ virtualItems, totalSize, measureElement }) => {
        const firstVirtualItem = virtualItems[0];
        const beforeSpacerHeight = firstVirtualItem?.start ?? 0;
        const lastVirtualItem = virtualItems[virtualItems.length - 1];
        const afterSpacerHeight = lastVirtualItem
          ? totalSize - lastVirtualItem.end
          : totalSize;

        return (
          <Box css={{ padding: '$half' }}>
            <Box
              as="table"
              css={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                tableLayout: 'auto',
              }}
              // +1 accounts for the <thead> row per WAI-ARIA spec
              aria-rowcount={items.length + 1}
            >
              {caption && (
                <VisuallyHidden as="caption">{caption}</VisuallyHidden>
              )}
              <Box
                as="thead"
                css={{
                  height: 0,
                  lineHeight: 0,
                  fontSize: 0,
                }}
              >
                <Box as="tr" css={{ height: 0 }}>
                  {columns.map((column) => (
                    <Box
                      key={column.id}
                      as="th"
                      scope="col"
                      css={{
                        textAlign: 'left',
                        padding: 0,
                        border: 0,
                        height: 0,
                        lineHeight: 0,
                      }}
                    >
                      <VisuallyHidden>
                        {formatMessage({ id: column.headerId })}
                      </VisuallyHidden>
                    </Box>
                  ))}
                </Box>
              </Box>
              {/* Spacer rows fill the off-screen space so the scrollbar
                  reflects the full list height. Inside <tbody> only <tr>
                  elements are valid, so padding/divs can't be used here. */}
              <Box as="tbody">
                {beforeSpacerHeight > 0 && (
                  <Box
                    as="tr"
                    aria-hidden="true"
                    css={{ height: beforeSpacerHeight }}
                  >
                    <Box as="td" />
                  </Box>
                )}
                {virtualItems.map((virtualItem) => (
                  <StatusReportTableRow
                    key={virtualItem.key}
                    ref={measureElement}
                    data-index={virtualItem.index}
                    item={items[virtualItem.index]}
                    statusTextMap={statusTextMap}
                    statusIconMap={statusIconMap}
                    columns={columns}
                    rendererRegistry={resolvedRendererRegistry}
                    // +2: 1-based index, offset by 1 for the <thead> row
                    aria-rowindex={virtualItem.index + 2}
                  />
                ))}
                {afterSpacerHeight > 0 && (
                  <Box
                    as="tr"
                    aria-hidden="true"
                    css={{ height: afterSpacerHeight }}
                  >
                    <Box as="td" />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      }}
    </VirtualizedAutoScrollBox>
  );
};
